import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import IconButton from "../../components/IconButton/IconButton";
import DishDetails from "../../components/DishDetails/DishDetails";
import Grid from "../../config/Grid/Grid";
import styles from "./Menu.module.scss";
import { useMenuData } from "@/hooks/useMenuData";
import { useDishReference } from "@/hooks/useDishReference";
import { useGoBack } from "@/hooks/useGoBack";
import {
  resolveSizesForDish,
  resolveToppingsForDish,
} from "@/helpers/dishOptions";
import {
  getTranslation,
  getTranslatedDescription,
} from "@/helpers/getTranslation";
import { formatGrosze } from "@/helpers/cartPricing";
import { menuPath } from "@/routes/paths";

/**
 * Browse-only dish detail. Same facts as the ordering screen, but nothing to
 * tap — so sizes and toppings are listed with their prices rather than being
 * pickers.
 */
function MenuDishPage() {
  const { restaurantId = "", dishId = "" } = useParams<{
    restaurantId: string;
    dishId: string;
  }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const goBack = useGoBack(menuPath(restaurantId));

  const { dishes, toppings, sizes, loading, error } = useMenuData(restaurantId);
  const reference = useDishReference(restaurantId);

  const dish = useMemo(
    () => dishes.find((candidate) => candidate.id === dishId),
    [dishes, dishId],
  );

  const dishSizes = useMemo(
    () => (dish ? resolveSizesForDish(dish, sizes) : []),
    [dish, sizes],
  );
  const dishToppings = useMemo(
    () => (dish ? resolveToppingsForDish(dish, toppings) : []),
    [dish, toppings],
  );

  return (
    <Grid>
      <div className={`start-1 col-4 ${styles.menuWrapper}`}>
        <div className={styles.menuHeaderContainer}>
          <div className={styles.menuHeader}>
            <IconButton
              iconName="las la-angle-left icon-big"
              onClick={goBack}
              color="primary"
              variant="text"
            />
            <p className={`body-bold-large ${styles.menuTitle}`}>
              {dish ? getTranslation(dish.translations, language) : t("menu.title")}
            </p>
            <IconButton
              iconName=""
              onClick={() => {}}
              color="primary"
              variant="text"
            />
          </div>
        </div>

        <div className={styles.menuContent}>
          {!dish && !loading ? (
            <p className={`body-medium ${styles.menuStateMessage}`}>
              {error ? t("order.loadError") : t("order.emptyMenu")}
            </p>
          ) : null}

          {dish ? (
            <div className={styles.dishDetailSection}>
              {dish.image?.url ? (
                <img
                  src={dish.image.url}
                  alt={getTranslation(dish.translations, language)}
                  className={styles.dishHeroImage}
                />
              ) : null}

              <div className={styles.dishPriceRow}>
                <p className={`title-medium ${styles.dishPriceLabel}`}>
                  {t("order.cart.subtotal")}
                </p>
                <p className={`title-medium ${styles.dishPriceValue}`}>
                  {formatGrosze(dish.price)}
                </p>
              </div>

              <DishDetails
                dish={dish}
                reference={reference}
                language={language}
                only="facts"
              />

              <p className={`body-medium ${styles.dishDescription}`}>
                {getTranslatedDescription(dish.translations, language)}
              </p>

              <DishDetails
                dish={dish}
                reference={reference}
                language={language}
                sizes={dishSizes}
                toppings={dishToppings}
                only="sections"
              />
            </div>
          ) : null}
        </div>
      </div>
    </Grid>
  );
}

export default MenuDishPage;
